import { BaseApiService } from '@/shared/api/BaseApiService';
import type {
  IPatientRepository,
  CreatePatientInput,
  UpdatePatientInput,
  UpdatePatientContactInput,
} from '../domain/repositories/patient.repository';
import type {
  Patient,
  PatientContact,
  PatientMedicalCondition,
  PatientAllergy,
  PatientVital,
  PatientClinicalFlag,
  MedicalCondition,
  Allergy,
  PatientListParams,
  PatientListResponse,
  VitalsHistoryResponse,
  Doctor,
} from '../domain/types';
import {
  toPatient,
  toPatientContact,
  toPatientMedicalCondition,
  toPatientAllergy,
  toPatientVital,
  toVitalsHistoryResponse,
  toDoctor,
  toPatientClinicalFlag,
} from './mappers/patient.mapper';

// ── DTO types (wire format, kept in infrastructure) ────────────────

import type {
  PatientResponse as PatientDto,
  PatientContactResponse as PatientContactDto,
} from '@dentexe/types';

export interface PatientListResponseDto {
  data: PatientDto[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface PatientMedicalConditionDto {
  id: string;
  organizationId: string;
  patientId: string;
  conditionId: string;
  notes: string | null;
  isActive: boolean;
}

export interface MedicalConditionDto {
  id: string;
  organizationId: string;
  name: string;
  description: string | null;
  severityCategory: string | null;
  icd10Code: string | null;
  requiresPremedication: boolean | null;
  requiresPhysicianClearance: boolean | null;
  bleedingRisk: boolean | null;
  anaesthesiaRisk: boolean | null;
  sortOrder: number;
  isCustom: boolean;
  isActive: boolean;
}

export interface PatientAllergyDto {
  id: string;
  organizationId: string;
  patientId: string;
  allergyId: string;
  reactionType: string | null;
  severity: string | null;
  notes: string | null;
  previousAnaestheticReaction: string | null;
  isActive: boolean;
}

export interface AllergyDto {
  id: string;
  organizationId: string;
  name: string;
  description: string | null;
  severityCategory: string | null;
  allergenCategory: string | null;
  typicalReaction: string | null;
  sortOrder: number;
  isCustom: boolean;
  isActive: boolean;
}

export interface PatientVitalDto {
  id: string;
  organizationId: string;
  clinicId: string;
  patientId: string;
  recordedAt: string;
  recordedBy: string | null;
  bpSystolic: number | null;
  bpDiastolic: number | null;
  pulseRate: number | null;
  weightKg: string | null;
  bloodSugarMgdl: number | null;
  smokingStatus: string | null;
  alcoholConsumption: string | null;
  diet: string | null;
  notes: string | null;
}

export interface ClinicalFlagTemplateDto {
  id: string;
  name: string;
  icon: string | null;
  color: string | null;
  sortOrder: number;
}

export interface PatientClinicalFlagDto {
  id: string;
  organizationId: string;
  patientId: string;
  flagId: string;
  value: string | null;
  notes: string | null;
  isActive: boolean;
  clinical_flag_templates?: ClinicalFlagTemplateDto | null;
}

export interface VitalsHistoryResponseDto {
  data: PatientVitalDto[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface DoctorDto {
  id: string;
  displayName: string;
}

// ── Repository implementation ─────────────────────────────────────

export class HttpPatientRepository extends BaseApiService implements IPatientRepository {
  protected readonly resourcePath = 'patients';

  async list(params: PatientListParams = {}): Promise<PatientListResponse> {
    const dto = await this.get<PatientListResponseDto>('', { params });
    return {
      data: dto.data.map(toPatient),
      total: dto.total,
      page: dto.page,
      limit: dto.limit,
      hasMore: dto.hasMore,
    };
  }

  async getById(id: string): Promise<Patient> {
    const dto = await this.get<PatientDto>(`/${id}`);
    return toPatient(dto);
  }

  async create(input: CreatePatientInput, idempotencyKey?: string): Promise<Patient> {
    const headers: Record<string, string> = {};
    if (idempotencyKey) {
      headers['Idempotency-Key'] = idempotencyKey;
    }
    const dto = await this.post<PatientDto>('', input, { headers });
    return toPatient(dto);
  }

  async update(id: string, input: UpdatePatientInput): Promise<Patient> {
    const dto = await this.patch<PatientDto>(`/${id}`, input);
    return toPatient(dto);
  }

  async remove(id: string): Promise<void> {
    return this.delete(`/${id}`);
  }

  async getContacts(patientId: string): Promise<PatientContact> {
    const dto = await this.get<PatientContactDto>(`/${patientId}/contacts`);
    return toPatientContact(dto);
  }

  async updateContacts(
    patientId: string,
    input: UpdatePatientContactInput,
  ): Promise<PatientContact> {
    const dto = await this.patch<PatientContactDto>(`/${patientId}/contacts`, input);
    return toPatientContact(dto);
  }

  async getMedicalConditions(patientId: string): Promise<PatientMedicalCondition[]> {
    const dto = await this.get<PatientMedicalConditionDto[]>(`/${patientId}/medical-conditions`);
    return dto.map(toPatientMedicalCondition);
  }

  async setMedicalConditions(
    patientId: string,
    input: { conditions: { name: string; notes?: string }[] },
  ): Promise<void> {
    return this.put(`/${patientId}/medical-conditions`, input);
  }

  async getAllergies(patientId: string): Promise<PatientAllergy[]> {
    const dto = await this.get<PatientAllergyDto[]>(`/${patientId}/allergies`);
    return dto.map(toPatientAllergy);
  }

  async setAllergies(
    patientId: string,
    input: {
      allergies: { name: string; reactionType?: string; severity?: string; notes?: string }[];
    },
  ): Promise<void> {
    return this.put(`/${patientId}/allergies`, input);
  }

  async getLatestVitals(patientId: string): Promise<PatientVital | null> {
    const dto = await this.get<PatientVitalDto | null>(`/${patientId}/vitals/latest`);
    return dto ? toPatientVital(dto) : null;
  }

  async getVitalsHistory(patientId: string, page = 1, limit = 20): Promise<VitalsHistoryResponse> {
    const dto = await this.get<VitalsHistoryResponseDto>(`/${patientId}/vitals/history`, {
      params: { page, limit },
    });
    return toVitalsHistoryResponse(dto);
  }

  async getClinicalFlags(patientId: string): Promise<PatientClinicalFlag[]> {
    const dto = await this.get<PatientClinicalFlagDto[]>(`/${patientId}/clinical-flags`);
    return dto.map(toPatientClinicalFlag);
  }

  async getDoctors(): Promise<Doctor[]> {
    const dto = await this.get<DoctorDto[]>('/meta/doctors');
    return dto.map(toDoctor);
  }
}

export class MedicalConditionsService extends BaseApiService {
  protected readonly resourcePath = 'medical-conditions';

  async list(): Promise<MedicalCondition[]> {
    const dto = await this.get<MedicalConditionDto[]>('');
    return dto;
  }

  async create(input: { name: string }): Promise<MedicalCondition> {
    const dto = await this.post<MedicalConditionDto>('', input);
    return dto;
  }

  async update(id: string, input: { name?: string }): Promise<MedicalCondition> {
    const dto = await this.patch<MedicalConditionDto>(`/${id}`, input);
    return dto;
  }

  async deactivate(id: string): Promise<MedicalCondition> {
    const dto = await this.delete<MedicalConditionDto>(`/${id}`);
    return dto;
  }

  async seed(): Promise<MedicalCondition[]> {
    const dto = await this.post<MedicalConditionDto[]>('/seed');
    return dto;
  }
}

export class AllergiesService extends BaseApiService {
  protected readonly resourcePath = 'allergies';

  async list(): Promise<Allergy[]> {
    const dto = await this.get<AllergyDto[]>('');
    return dto;
  }

  async create(input: { name: string }): Promise<Allergy> {
    const dto = await this.post<AllergyDto>('', input);
    return dto;
  }

  async update(id: string, input: { name?: string }): Promise<Allergy> {
    const dto = await this.patch<AllergyDto>(`/${id}`, input);
    return dto;
  }

  async deactivate(id: string): Promise<Allergy> {
    const dto = await this.delete<AllergyDto>(`/${id}`);
    return dto;
  }

  async seed(): Promise<Allergy[]> {
    const dto = await this.post<AllergyDto[]>('/seed');
    return dto;
  }
}
